import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, API_BASE_IOTHUB, authHeaders } from '../constants/api';
import { Suche, Hinweis, Export, Abbrechen } from '../icons';
import { useDropdownClose } from '../hooks/useDropdownClose';
import ErrorBoundary from './ErrorBoundary';
import './ExportData.css';

const AGG_FUNCTIONS = [
  { value: 'AVG', label: 'Durchschnitt' },
  { value: 'SUM', label: 'Summe' },
  { value: 'MIN', label: 'Minimum' },
  { value: 'MAX', label: 'Maximum' },
  { value: 'COUNT', label: 'Anzahl' },
];

const AGG_INTERVAL_UNITS = [
  { value: '1000', label: 'Sekunden' },
  { value: '60000', label: 'Minuten' },
  { value: '3600000', label: 'Stunden' },
  { value: '86400000', label: 'Tage' },
];

const TooltipIcon = ({ text, idSuffix, disabled = false }) => {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);
  const btnRef = useRef(null);
  const containerClass = `export-tooltip-${idSuffix}`;
  useDropdownClose(show && !disabled, () => setShow(false), `.${containerClass}`);

  useEffect(() => {
    if (!show || disabled) return;
    const tooltipEl = tooltipRef.current;
    const btnEl = btnRef.current;
    if (!tooltipEl || !btnEl) return;
    function positionTooltip() {
      const winW = window.innerWidth;
      const margin = 16;
      const btnRect = btnEl.getBoundingClientRect();
      const tooltipW = Math.min(300, Math.max(160, winW - 32));
      let left = Math.round(btnRect.left + btnRect.width / 2 - tooltipW / 2);
      if (left < margin) left = margin;
      if (left + tooltipW > winW - margin) left = winW - margin - tooltipW;
      tooltipEl.style.position = 'fixed';
      tooltipEl.style.top = `${Math.round(btnRect.bottom + 6)}px`;
      tooltipEl.style.left = `${left}px`;
      tooltipEl.style.width = `${tooltipW}px`;
      tooltipEl.style.right = 'auto';
      tooltipEl.style.transform = 'none';
    }
    positionTooltip();
    window.addEventListener('resize', positionTooltip);
    window.addEventListener('scroll', positionTooltip, true);
    return () => {
      window.removeEventListener('resize', positionTooltip);
      window.removeEventListener('scroll', positionTooltip, true);
    };
  }, [show, disabled]);

  return (
    <span className={containerClass}>
      <button
        ref={btnRef}
        type="button"
        className={`export-tooltip-btn${show ? ' active' : ''}${disabled ? ' export-tooltip-btn--disabled' : ''}`}
        aria-label="Information"
        title={disabled ? undefined : 'Information anzeigen'}
        aria-describedby={show ? `export-tooltip-content-${idSuffix}` : undefined}
        aria-expanded={show}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => { if (!disabled) setShow(prev => !prev); }}
        onKeyDown={e => { if (disabled) return; if (e.key === 'Escape') setShow(false); }}
      >
        <Hinweis />
      </button>
      {show && !disabled && (
        <div
          id={`export-tooltip-content-${idSuffix}`}
          role="tooltip"
          ref={tooltipRef}
          className="export-tooltip-box"
        >
          {text}
        </div>
      )}
    </span>
  );
};

function SectionHeader({ number, title, tooltip, required, tooltipDisabled = false }) {
  return (
    <div className="export-field-label">
      <span className="export-field-number">{number}</span>
      <span>{title}{required && <span className="export-required">*</span>}</span>
      {tooltip && <TooltipIcon text={tooltip} idSuffix={number.replace(/[^\w]/g, '')} disabled={tooltipDisabled} />}
    </div>
  );
}

function SearchDropdown({ id, label, placeholder, inputValue, onInputChange, options, onSelect, onClear, disabled, invalid, onBlurCallback }) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = `export-dd-list-${id}`;
  useEffect(() => {
    if (highlighted < 0 || !listRef.current) return;
    listRef.current.children[highlighted]?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const filtered = options.filter(o =>
    !inputValue || o.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  const focusNext = useCallback(() => {
    setTimeout(() => {
      const all = Array.from(document.querySelectorAll(
        'input[type="text"]:not([disabled]), input[type="number"]:not([disabled]), input[type="date"]:not([disabled]), input[type="time"]:not([disabled]), textarea:not([disabled]), select:not([disabled])'
      ));
      const idx = all.indexOf(inputRef.current);
      if (idx >= 0) {
        for (let i = idx + 1; i < all.length; i++) {
          all[i].focus();
          break;
        }
      }
    }, 0);
  }, []);

  const selectItem = useCallback((option) => {
    onSelect(option);
    setOpen(false);
    setHighlighted(-1);
    focusNext();
  }, [onSelect, focusNext]);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlighted(0); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted >= 0 && highlighted < filtered.length) {
        selectItem(filtered[highlighted]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlighted(-1);
    }
  };

  const handleBlur = (e) => {
    if (!wrapperRef.current?.contains(e.relatedTarget)) {
      setOpen(false);
      setHighlighted(-1);
      if (typeof onBlurCallback === 'function') onBlurCallback();
    }
  };

  return (
    <div className="modal-device-search-wrapper" ref={wrapperRef} onBlur={handleBlur}>
      {label && (
        <label className="modal-device-search-label" htmlFor={`export-dd-${id}`}>
          {label}
        </label>
      )}
      <span className="modal-device-search-icon" aria-hidden="true">
        <Suche />
      </span>
      <input
        ref={inputRef}
        type="text"
        id={`export-dd-${id}`}
        className={`modal-device-search-input${invalid ? ' modal-device-search-input--invalid' : ''}`}
        value={inputValue}
        onChange={e => { onInputChange(e.target.value); setOpen(true); setHighlighted(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-expanded={open}
      />
      {inputValue && !disabled && (
        <button
          type="button"
          className="modal-device-search-clear"
          aria-label="Auswahl zurücksetzen"
          onMouseDown={e => { e.preventDefault(); onClear(); inputRef.current?.focus(); }}
        >
          ✖
        </button>
      )}
      {open && !disabled && filtered.length > 0 && (
        <div className="modal-device-search-suggestions" id={listboxId} role="listbox" ref={listRef}>
          {filtered.map((o, idx) => (
            <div
              key={o.value}
              className={`modal-device-search-suggestion${idx === highlighted ? ' highlighted' : ''}`}
              role="option"
              aria-selected={idx === highlighted}
              onMouseDown={() => selectItem(o)}
              onMouseEnter={() => setHighlighted(idx)}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
      {open && !disabled && inputValue && filtered.length === 0 && (
        <div className="modal-device-search-suggestions" role="listbox" aria-label="Keine Ergebnisse">
          <div className="modal-device-search-no-results">Keine Ergebnisse</div>
        </div>
      )}
    </div>
  );
}

function ExportData() {
  const { authToken: token, iotHubToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { prefillDeviceId, prefillDeviceName } = location.state || {};

  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceText, setDeviceText] = useState('');

  const [availableKeys, setAvailableKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [keyText, setKeyText] = useState('');
  const [allKeysSelected, setAllKeysSelected] = useState(false);

  const [exportFormat, setExportFormat] = useState('JSON');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');

  const [dataLimit, setDataLimit] = useState('');
  const [sortOrder, setSortOrder] = useState('ASC');

  const [aggregate, setAggregate] = useState('NO');
  const [aggFunction, setAggFunction] = useState('');
  const [aggFunctionText, setAggFunctionText] = useState('');
  const [aggUnitMs, setAggUnitMs] = useState('');
  const [aggUnitText, setAggUnitText] = useState('');
  const [aggInterval, setAggInterval] = useState('');

  const [showErrors, setShowErrors] = useState(false);
  const [deviceTouched, setDeviceTouched] = useState(false);
  const [aggFunctionTouched, setAggFunctionTouched] = useState(false);
  const [aggUnitTouched, setAggUnitTouched] = useState(false);
  const [dataLimitTouched, setDataLimitTouched] = useState(false);
  const [aggIntervalTouched, setAggIntervalTouched] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportSuccess, setExportSuccess] = useState('');

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/tenant/devices?pageSize=1000&page=0`, { headers: authHeaders(token) })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          const mapped = (data.data || []).map(d => ({ value: d.id.id, label: d.name }));
          setDevices(mapped);
          if (prefillDeviceId) {
            const match = mapped.find(d => d.value === prefillDeviceId)
              || (prefillDeviceName ? { value: prefillDeviceId, label: prefillDeviceName } : null);
            if (match) {
              setSelectedDevice(match);
              setDeviceText(match.label);
            }
          }
        }
      })
      .catch(() => {});
  }, [token, prefillDeviceId, prefillDeviceName]);

  useEffect(() => {
    setAvailableKeys([]);
    setSelectedKeys([]);
    setAllKeysSelected(false);
    if (!token || !selectedDevice) return;
    fetch(
      `${API_BASE}/plugins/telemetry/DEVICE/${selectedDevice.value}/keys/timeseries`,
      { headers: authHeaders(token) }
    )
      .then(r => r.ok ? r.json() : [])
      .then(keys => setAvailableKeys(keys || []))
      .catch(() => {});
  }, [selectedDevice, token]);

  const handleKeySelect = (option) => {
    if (!selectedKeys.includes(option.value)) {
      setSelectedKeys(prev => [...prev, option.value]);
    }
    setKeyText('');
    setAllKeysSelected(false);
  };

  const removeKey = (key) => {
    setSelectedKeys(prev => prev.filter(k => k !== key));
    setAllKeysSelected(false);
  };

  const handleAllKeys = (checked) => {
    setAllKeysSelected(checked);
    if (!checked) setSelectedKeys([]);
  };

  const INTERVAL_TYPE_MAP = {
    '1000': 'SECOND',
    '60000': 'MINUTE',
    '3600000': 'HOUR',
    '86400000': 'DAY',
  };

  const dataLimitInvalid = dataLimit !== '' && (
    !/^\d+$/.test(dataLimit) || Number(dataLimit) < 1 || Number(dataLimit) > 50000
  );
  const aggIntervalInvalid = aggInterval !== '' && (
    !/^\d+$/.test(aggInterval) || Number(aggInterval) < 1
  );

  const deviceError = (showErrors || deviceTouched) && !selectedDevice;
  const keysError = showErrors && !!selectedDevice && !allKeysSelected && selectedKeys.length === 0;
  const timespanError = showErrors && (!startDate || !endDate);
  const dataLimitError = (showErrors || dataLimitTouched) && dataLimitInvalid;
  const aggFunctionError = (showErrors || aggFunctionTouched) && aggregate === 'YES' && !aggFunction;
  const aggUnitError = (showErrors || aggUnitTouched) && aggregate === 'YES' && !aggUnitMs;
  const aggIntervalError = (showErrors && aggregate === 'YES' && !aggInterval) ||
    ((showErrors || aggIntervalTouched) && aggIntervalInvalid);

  const handleExport = async (e) => {
    e.preventDefault();
    setExportError('');
    setExportSuccess('');

    const hasErrors =
      !selectedDevice ||
      (!!selectedDevice && !allKeysSelected && selectedKeys.length === 0) ||
      !startDate || !endDate ||
      dataLimitInvalid ||
      (aggregate === 'YES' && (!aggFunction || !aggUnitMs || !aggInterval || aggIntervalInvalid));

    if (hasErrors) {
      setShowErrors(true);
      return;
    }

    const params = new URLSearchParams();

    // "Alle" muss ausgeschrieben werden: ThingsBoards Zeitbereichsabfrage
    // liefert ohne keys eine leere Antwort, und der Export war damit auf dem
    // Standardweg eine leere Datei mit HTTP 200.
    const keys = allKeysSelected ? availableKeys : selectedKeys;
    if (keys.length === 0) {
      setExportError('Für dieses Gerät sind keine Messwerte bekannt.');
      return;
    }
    params.set('keys', keys.join(','));

    params.set('format', exportFormat.toLowerCase());
    params.set('timeZone', 'Europe/Berlin');
    params.set('orderBy', sortOrder);

    params.set('startTs', new Date(`${startDate}T${startTime || '00:00'}:00`).getTime());
    params.set('endTs', new Date(`${endDate}T${endTime || '23:59'}:59`).getTime());
    if (dataLimit) params.set('limit', dataLimit);

    if (aggregate === 'YES' && aggFunction) {
      params.set('agg', aggFunction);
      if (aggUnitMs && aggInterval) {
        params.set('intervalType', INTERVAL_TYPE_MAP[aggUnitMs] || 'MILLISECOND');
        params.set('interval', aggInterval);
      }
    } else {
      params.set('agg', 'NONE');
    }

    try {
      setExporting(true);

      const res = await fetch(
        `${API_BASE_IOTHUB}/api/v1/telemetry/${selectedDevice.value}/export?${params}`,
        { headers: authHeaders(iotHubToken) }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedDevice.label}_export.${exportFormat.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShowErrors(false);
      setExportSuccess('Daten wurden erfolgreich exportiert.');
    } catch (err) {
      setExportError(`Export fehlgeschlagen: ${err.message || 'Netzwerkfehler'}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="export-data">
      <div className="device-toolbar">
        <div className="device-toolbar-left">
          <h1 className="device-title">Daten als Datei exportieren</h1>
        </div>
      </div>

      <form className="export-form" onSubmit={handleExport} noValidate>

        {/* Zeile 1: Gerät | Datenschlüssel | Exportformat */}
        <div className="export-grid-row export-grid-row--3col">

          <div className="export-section">
            <SectionHeader number="❶" title="Gerät auswählen" required tooltip="Wähle das Gerät aus, dessen Telemetriedaten exportiert werden sollen. Aktuell kann nur ein Gerät für den Export ausgewählt werden." />
            <SearchDropdown
              id="device"
              label="Gerät"
              placeholder="Gerät suchen …"
              inputValue={deviceText}
              onInputChange={text => { setDeviceText(text); setSelectedDevice(null); }}
              options={devices}
              onSelect={o => { setSelectedDevice(o); setDeviceText(o.label); }}
              onClear={() => { setSelectedDevice(null); setDeviceText(''); }}
              invalid={deviceError}
              onBlurCallback={() => setDeviceTouched(true)}
            />
            {deviceError && (
              <div className="export-error-text" role="alert">
                {deviceText ? 'Bitte einen Eintrag aus der Liste auswählen.' : 'Dieses Feld ist erforderlich.'}
              </div>
            )}
          </div>

          <div className={`export-section${!selectedDevice ? ' export-section--disabled' : ''}`}>
            <SectionHeader
              number="❷"
              title="Datenschlüssel wählen"
              required
              tooltip="Wähle die Datenschlüssel aus, die exportiert werden sollen. Du kannst mehrere Schlüssel auswählen oder alle auf einmal."
              tooltipDisabled={!selectedDevice}
            />
            <SearchDropdown
              id="key"
              label="Datenschlüssel"
              placeholder="Datenschlüssel suchen …"
              inputValue={keyText}
              onInputChange={setKeyText}
              options={availableKeys
                .filter(k => !selectedKeys.includes(k))
                .map(k => ({ value: k, label: k }))}
              onSelect={handleKeySelect}
              onClear={() => setKeyText('')}
              disabled={!selectedDevice || allKeysSelected}
            />
            {selectedKeys.length > 0 && !allKeysSelected && (
              <div className="export-key-chips">
                {selectedKeys.map(k => (
                  <span key={k} className="export-key-chip">
                    {k}
                    <button
                      type="button"
                      className="export-key-chip-remove"
                      onClick={() => removeKey(k)}
                      aria-label={`${k} entfernen`}
                    >
                      ✖
                    </button>
                  </span>
                ))}
              </div>
            )}
            <label className="export-checkbox-label">
              <input
                type="checkbox"
                checked={allKeysSelected}
                onChange={e => handleAllKeys(e.target.checked)}
                disabled={!selectedDevice || availableKeys.length === 0}
              />
              Alle Datenschlüssel wählen
            </label>
            {keysError && (
              <div className="export-error-text" role="alert">
                Bitte wählen Sie mindestens einen Datenschlüssel aus oder aktivieren Sie „Alle Datenschlüssel wählen".
              </div>
            )}
          </div>

          <div className="export-section">
            <SectionHeader number="❸" title="Exportformat wählen" tooltip="Wähle das gewünschte Dateiformat für den Export." />
            <div className="export-radio-grid">
              {['JSON', 'CSV', 'ODS', 'XLSX'].map(fmt => (
                <label key={fmt} className="export-radio-label">
                  <input
                    type="radio"
                    name="export-format"
                    value={fmt}
                    checked={exportFormat === fmt}
                    onChange={() => setExportFormat(fmt)}
                  />
                  {fmt}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Zeile 2: Zeitspanne | Datenlimit | Sortierung */}
        <div className="export-grid-row export-grid-row--middle">

          <div className="export-section">
            <SectionHeader
              number="❹"
              title="Zeitspanne"
              required
              tooltip="Lege den Zeitraum fest, für den Daten exportiert werden sollen. Start- und Enddatum sind Pflichtangaben. Wenn Du den Export auf einen bestimmten Zeitraum innerhalb der Tage beschränken möchtest, kannst Du zusätzlich eine Start- und Endzeit angeben."
            />
            <div className="export-time-grid">
              <div className="export-input-wrapper">
                <span className="export-floating-label">Startdatum</span>
                <input type="date" className={`export-input${timespanError ? ' export-input--invalid' : ''}`} value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="export-input-wrapper">
                <span className="export-floating-label">Enddatum</span>
                <input type="date" className={`export-input${timespanError ? ' export-input--invalid' : ''}`} value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
              <div className="export-input-wrapper">
                <span className="export-floating-label">Startzeit</span>
                <input type="time" className="export-input" value={startTime} onChange={e => setStartTime(e.target.value)} />
              </div>
              <div className="export-input-wrapper">
                <span className="export-floating-label">Endzeit</span>
                <input type="time" className="export-input" value={endTime} onChange={e => setEndTime(e.target.value)} />
              </div>
            </div>
            {timespanError && (
              <div className="export-error-text" role="alert">Start- und Enddatum sind erforderlich.</div>
            )}
          </div>

          <div className="export-section">
            <SectionHeader
              number="❺"
              title="Datenlimit"
              tooltip="Begrenze die Anzahl der exportierten Datenpunkte. Erlaubt sind Werte von 1 bis 50.000. Wenn keine Angabe erfolgt, wird der Standardwert von 50.000 verwendet."
            />
            <div className="export-input-wrapper">
              <span className="export-floating-label">Limit</span>
              <input
                type="number"
                className={`export-input${dataLimitError ? ' export-input--invalid' : ''}`}
                value={dataLimit}
                onChange={e => setDataLimit(e.target.value)}
                onBlur={() => setDataLimitTouched(true)}
                placeholder="z.B. 1000"
                min="1"
                max="50000"
                step="1"
              />
            </div>
            {dataLimitError && (
              <div className="export-error-text" role="alert">Bitte einen ganzzahligen Wert zwischen 1 und 50.000 eingeben.</div>
            )}
          </div>

          <div className="export-section">
            <SectionHeader number="❻" title="Sortierung der Daten" tooltip="Legt fest, ob die Datenpunkte aufsteigend (älteste zuerst) oder absteigend (neueste zuerst) nach Zeitstempel sortiert werden." />
            <div className="export-radio-group">
              <label className="export-radio-label">
                <input
                  type="radio"
                  name="sort-order"
                  value="ASC"
                  checked={sortOrder === 'ASC'}
                  onChange={() => setSortOrder('ASC')}
                />
                Aufsteigend
              </label>
              <label className="export-radio-label">
                <input
                  type="radio"
                  name="sort-order"
                  value="DESC"
                  checked={sortOrder === 'DESC'}
                  onChange={() => setSortOrder('DESC')}
                />
                Absteigend
              </label>
            </div>
          </div>
        </div>

        {/* Aggregation */}
        <h2 className="export-agg-heading">Aggregation</h2>

        {/* Zeile: Feld 7 – allein */}
        <div className="export-grid-row export-grid-row--agg-toggle">
          <div className="export-section">
            <SectionHeader number="❼" title="Daten aggregieren?" tooltip="Aggregation fasst mehrere Datenpunkte innerhalb eines Zeitintervalls zu einem einzelnen Wert zusammen. So kann beispielsweise aus allen Messwerten eines Tages ein Tagesdurchschnitt berechnet werden" />
            <div className="export-radio-group">
              <label className="export-radio-label">
                <input
                  type="radio"
                  name="aggregate"
                  value="YES"
                  checked={aggregate === 'YES'}
                  onChange={() => setAggregate('YES')}
                />
                Ja
              </label>
              <label className="export-radio-label">
                <input
                  type="radio"
                  name="aggregate"
                  value="NO"
                  checked={aggregate === 'NO'}
                  onChange={() => setAggregate('NO')}
                />
                Nein
              </label>
            </div>
          </div>
        </div>

        {/* Zeile: Felder 8-10 (nur bei Aggregation = Ja) */}
        {aggregate === 'YES' && (
          <div className="export-grid-row export-grid-row--3col">
            <div className="export-section">
              <SectionHeader number="❽" title="Aggregationsfunktion" required tooltip="Lege fest, wie die Datenpunkte innerhalb eines Aggregationsintervalls zusammengefasst werden. Zur Auswahl stehen beispielsweise Durchschnitt, Summe oder Maximum." />
              <SearchDropdown
                id="agg-fn"
                label="Funktion"
                placeholder="Funktion suchen …"
                inputValue={aggFunctionText}
                onInputChange={text => { setAggFunctionText(text); setAggFunction(''); }}
                options={AGG_FUNCTIONS}
                onSelect={o => { setAggFunction(o.value); setAggFunctionText(o.label); }}
                onClear={() => { setAggFunction(''); setAggFunctionText(''); }}
                invalid={aggFunctionError}
                onBlurCallback={() => setAggFunctionTouched(true)}
              />
              {aggFunctionError && (
                <div className="export-error-text" role="alert">
                  {aggFunctionText ? 'Bitte einen Eintrag aus der Liste auswählen.' : 'Dieses Feld ist erforderlich.'}
                </div>
              )}
            </div>

            <div className="export-section">
              <SectionHeader number="❾" title="Aggregationstyp" required tooltip="Wähle die Zeiteinheit für das Aggregationsintervall. Mögliche Einheiten sind beispielsweise Minuten, Stunden oder Tage." />
              <SearchDropdown
                id="agg-unit"
                label="Typ"
                placeholder="Typ suchen …"
                inputValue={aggUnitText}
                onInputChange={text => { setAggUnitText(text); setAggUnitMs(''); }}
                options={AGG_INTERVAL_UNITS}
                onSelect={o => { setAggUnitMs(o.value); setAggUnitText(o.label); }}
                onClear={() => { setAggUnitMs(''); setAggUnitText(''); }}
                invalid={aggUnitError}
                onBlurCallback={() => setAggUnitTouched(true)}
              />
              {aggUnitError && (
                <div className="export-error-text" role="alert">
                  {aggUnitText ? 'Bitte einen Eintrag aus der Liste auswählen.' : 'Dieses Feld ist erforderlich.'}
                </div>
              )}
            </div>

            <div className="export-section">
              <SectionHeader
                number="❿"
                title="Intervall"
                required
                tooltip="Lege die Größe des Aggregationsintervalls fest. Zusammen mit dem Aggregationstyp bestimmt dieser Wert den Zeitraum der Aggregation (z.B. 5 Minuten oder 1 Stunde)."
              />
              <div className="export-input-wrapper">
                <span className="export-floating-label">Intervall</span>
                <input
                  type="number"
                  className={`export-input${aggIntervalError ? ' export-input--invalid' : ''}`}
                  value={aggInterval}
                  onChange={e => setAggInterval(e.target.value)}
                  onBlur={() => setAggIntervalTouched(true)}
                  placeholder="z.B. 7"
                  min="1"
                  step="1"
                />
              </div>
              {aggIntervalError && (
                <div className="export-error-text" role="alert">
                  {aggIntervalInvalid ? 'Bitte eine positive ganze Zahl eingeben.' : 'Dieses Feld ist erforderlich.'}
                </div>
              )}
            </div>
          </div>
        )}

        {exportError && <p className="export-error" role="alert">{exportError}</p>}
        {exportSuccess && <p className="export-success" role="status">{exportSuccess}</p>}

        {/* Pflichtfeld-Hinweis */}
        <div className="export-required-explain" aria-hidden>
          <span className="export-required">*</span> Pflichtfeld
        </div>

        {showErrors && (
          <p className="export-required-fields-error" role="alert">
            Bitte fülle alle Pflichtfelder aus.
          </p>
        )}

        <div className="export-form-actions">
          <button type="submit" className="export-btn-primary" disabled={exporting}>
            <Export />
            {exporting ? 'Exportiere …' : 'Daten exportieren'}
          </button>
          <button type="button" className="export-btn-secondary" onClick={() => navigate(-1)}>
            <Abbrechen /> Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
}

export default function ExportDataWithBoundary() {
  return <ErrorBoundary><ExportData /></ErrorBoundary>;
}
